import { getMobileSize } from "desktopWindow/desktopWindowUtils";
import * as React from "react";
import { isMobile } from "react-device-detect";
import { Rnd } from "react-rnd";
import { DesktopWindowProvider, useDesktopWindow } from "../context/DesktopWindowContext";
import { useWindowTile } from "../context/WindowTilesContext";
import {
	WindowContent as DesktopWindowContent,
	WindowTitleBar as DesktopWindowTitleBar,
	WindowContainer,
} from "./DesktopWindow.styled";

interface Props {
	id: string;
	className?: string;
}

let latestZindex = 1;

export const DesktopWindow: React.FC<Props> = ({ id, children, ...props }) => {
	const {
		open,
		size: { height = isMobile ? getMobileSize().height : 400, width = isMobile ? getMobileSize().width : 600 },
		minSize,
		position: { x = -300, y = -600 },
		resizeable = true,
		onResizeStop,
		focusedTileId,
		setWindowToFocus,
		draggable = true,
	} = useWindowTile(id);
	const [zIndex, setzIndex] = React.useState<number>(0);
	const focus = id === focusedTileId;
	React.useEffect(() => {
		if (!open) {
			setzIndex(-9999);
			return;
		}
		latestZindex += 1;
		setzIndex(latestZindex);
	}, [open]);

	React.useEffect(() => {
		if (focusedTileId === id) {
			latestZindex += 1;
			setzIndex(latestZindex);
		}
	}, [focusedTileId, id]);

	const setWindowFocus = () => {
		latestZindex += 1;
		if (!focus) {
			setzIndex(latestZindex);
			setWindowToFocus(id);
		}
	};

	return (
		<DesktopWindowProvider tileId={id} isFocus={focus!}>
			<Rnd
				className={props.className}
				default={{ width, height: height, x, y }}
				minHeight={isMobile ? height : minSize?.height}
				minWidth={isMobile ? undefined : minSize?.width}
				dragHandleClassName={"title-bar"}
				onResizeStart={() => setWindowFocus()}
				onResizeStop={onResizeStop}
				onMouseDown={() => setWindowFocus()}
				style={{ zIndex }}
				bounds="window"
				enableResizing={resizeable}
				disableDragging={draggable}
			>
				<WindowContainer id={"dialog-game-" + props.className} onClick={setWindowFocus}>
					{children}
				</WindowContainer>
			</Rnd>
		</DesktopWindowProvider>
	);
};

export const WindowTitleBar = (props: { icon?: JSX.Element; children?: React.ReactNode }) => {
	const { windowTitle: title, minimize: onMinimize, close: onClose } = useDesktopWindow();
	return (
		<DesktopWindowTitleBar
			icon={props.icon}
			title={title}
			onClose={onClose}
			onMinimize={onMinimize}
			children={props.children}
		/>
	);
};

export const WindowContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>((props, ref) => {
	const { verticalScroll = true, horizontalScroll = true } = useDesktopWindow();
	return (
		<DesktopWindowContent
			ref={ref}
			verticalScroll={verticalScroll}
			horizontalScroll={horizontalScroll}
			{...props}
		/>
	);
});
